import { MIPCreateContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/MIPCreateContext';
import { MIP_CREATE_ROUTES } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/useMIPCreateController';
import {
	SAVE_FAILED,
	SAVE_OK,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardSaveResult } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import {
	createKycFormsTemplates,
	deleteKycFormsTemplates,
	getMIPKycFormsTemplates,
	updateKycFormsTemplates,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	useTranslationCommonUi,
	useTranslationMipCreate,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	CustomDetailCategory,
	DocumentWorkflowTemplateSourceFormat,
	DocumentWorkflowTemplateVisibleIn,
	KycFormTemplateRes,
} from '@dealstack/shared/lib/generated';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { isEqual } from 'lodash';
import { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { isNotEmpty } from '@dealstack/shared/lib/isNotEmpty.ts';
import { useMutation } from '@tanstack/react-query';
import { CustomDetailEnum } from '@dealstack/shared/lib/generated/models/CustomDetailEnum.ts';
import type { KycPlacementValue } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/KYCRequirements/common/FormsTemplatesList/SignaturePlacement/kycSignaturePlacement.types.ts';

export enum KYC_FORM_FIELDS {
	individualFormsTemplates = 'individualFormsTemplates',
	pivFormsTemplates = 'pivFormsTemplates',
}

export type FormTemplate = {
	id: string;
	fileId?: string | null;
	fileName?: string | null;
	name: string;
	instruction?: string;
	archived?: boolean;
	type: CustomDetailEnum;
	category?: CustomDetailCategory | null;
	isPreFillable?: boolean;
	requiresSignature?: boolean;
	useManualSignaturePlacement?: boolean;
	/** Drives the stamp aspect ratio, so the editor has to reopen in the mode the box was drawn in. */
	showStampMetadata?: boolean;
	/** Which field-binding editor this form opens: a coordinate canvas, or its own document. */
	sourceFormat?: DocumentWorkflowTemplateSourceFormat;
	/** Box placed before the template exists; sent with the create call. */
	pendingPlacement?: KycPlacementValue | null;
	local?: boolean;
};

export type KycFormValues = {
	[KYC_FORM_FIELDS.individualFormsTemplates]: FormTemplate[];
	[KYC_FORM_FIELDS.pivFormsTemplates]: FormTemplate[];
};

export const useKYCDocumentsController = () => {
	const mipCreateController = useContext(MIPCreateContext);
	const tCommonUi = useTranslationCommonUi();
	const tMipCreate = useTranslationMipCreate();

	const getKycFormsTemplatesRequest = useWrappedRequest(getMIPKycFormsTemplates);
	const createKycFormsTemplatesRequest = useMutation({
		mutationFn: (variables: Parameters<typeof createKycFormsTemplates>[0]) =>
			createKycFormsTemplates(variables),
	});
	const updateKycFormsTemplatesRequest = useMutation({
		mutationFn: (variables: Parameters<typeof updateKycFormsTemplates>[0]) =>
			updateKycFormsTemplates(variables),
	});
	const deleteCustomDetailsRequest = useMutation({
		mutationFn: (variables: Parameters<typeof deleteKycFormsTemplates>[0]) =>
			deleteKycFormsTemplates(variables),
	});

	const kycForm = useForm<KycFormValues>({
		mode: 'all',
		defaultValues: {
			individualFormsTemplates: [],
			pivFormsTemplates: [],
		},
	});

	const setKycFormValues = (data?: KycFormTemplateRes[]) => {
		if (!data?.length) {
			return;
		}

		const formValues: KycFormValues = data.reduce(
			(acc: KycFormValues, detail) => {
				const type = detail.visibleIn.includes(
					DocumentWorkflowTemplateVisibleIn.KYC_VEHICLE
				)
					? CustomDetailEnum.VEHICLE
					: CustomDetailEnum.INDIVIDUAL;

				const detailData: FormTemplate = {
					id: detail.id,
					name: detail.name,
					type,
					instruction: detail.instruction ?? undefined,
					fileId: detail.fileId,
					fileName: detail.fileName,
					category: detail.category,
					isPreFillable: detail.isPreFillable,
					requiresSignature: detail.requiresSignature,
					useManualSignaturePlacement: detail.useManualSignaturePlacement,
					showStampMetadata: detail.showStampMetadata,
					sourceFormat: detail.sourceFormat,
				};

				if (type === CustomDetailEnum.INDIVIDUAL) {
					return {
						...acc,
						individualFormsTemplates: [...acc.individualFormsTemplates, detailData],
					};
				}

				return {
					...acc,
					pivFormsTemplates: [...acc.pivFormsTemplates, detailData],
				};
			},
			{
				individualFormsTemplates: [],
				pivFormsTemplates: [],
			}
		);

		kycForm.reset(formValues);
	};

	useEffect(() => {
		void (async () => {
			const backupData = mipCreateController?.dataBackup?.[MIP_CREATE_ROUTES.KYC];

			if (backupData) {
				kycForm.reset({
					individualFormsTemplates: backupData.individualFormsTemplates ?? [],
					pivFormsTemplates: backupData.pivFormsTemplates ?? [],
				});
				return;
			}
			const backendData = await getKycFormsTemplatesRequest
				.fetch({ archived: false })
				.concludeWithPromise();

			setKycFormValues(backendData?.payload);
		})();
	}, []);

	const saveData = async (): Promise<{ isSuccess: boolean; formValues: KycFormValues }> => {
		const originalFormsTemplates = getKycFormsTemplatesRequest.response?.payload;
		mipCreateController.setIsLoading(true);
		let isAnyFailed = false;

		const formValues: KycFormValues = kycForm.getValues();

		const formsTemplates = [
			...formValues.individualFormsTemplates,
			...formValues.pivFormsTemplates,
		];

		const formsToCreate = formsTemplates
			.filter(
				(f): f is FormTemplate & { fileId: string } => !!(f.local && f.name && f.fileId)
			)
			.map((form) => ({
				name: form.name,
				instruction: form.instruction ?? '',
				type: form.type,
				fileId: form.fileId,
				category: form.category ?? null,
				requiresSignature: form.requiresSignature ?? false,
				useManualSignaturePlacement: form.useManualSignaturePlacement ?? false,
				showStampMetadata: form.showStampMetadata ?? true,
				// The box was placed before this template existed; the backend keys it to the
				// PARTY role it creates.
				pendingSignaturePlacements: form.pendingPlacement ? [form.pendingPlacement] : [],
			}));

		const formsToDelete = originalFormsTemplates
			?.filter((of) => !formsTemplates?.some((f) => of.id === f.id))
			.map((form) => form.id);

		const formsToUpdate = formsTemplates
			.filter((formTemplate) => {
				if (!formTemplate.name || formTemplate.local) {
					return false;
				}
				const originalFormTemplate = originalFormsTemplates?.find(
					(of) => of.id === formTemplate.id
				);
				if (!originalFormTemplate) {
					return false;
				}

				// A box placed in the drawer is a change on its own — every field compared below can
				// be identical while the placement is the only thing the admin touched.
				if (formTemplate.pendingPlacement) {
					return true;
				}

				return !isEqual(
					{
						name: formTemplate.name,
						instruction: formTemplate.instruction ?? '',
						type: formTemplate.type,
						category: formTemplate.category ?? null,
						requiresSignature: formTemplate.requiresSignature ?? false,
						// Clearing the box is a change whose only trace is this flag going false —
						// without it the update is filtered out and the placement silently survives.
						useManualSignaturePlacement:
							formTemplate.useManualSignaturePlacement ?? false,
					},
					{
						name: originalFormTemplate.name,
						instruction: originalFormTemplate.instruction ?? '',
						type: originalFormTemplate.visibleIn.includes(
							DocumentWorkflowTemplateVisibleIn.KYC_VEHICLE
						)
							? CustomDetailEnum.VEHICLE
							: CustomDetailEnum.INDIVIDUAL,
						category: originalFormTemplate.category ?? null,
						requiresSignature: originalFormTemplate.requiresSignature,
						useManualSignaturePlacement:
							originalFormTemplate.useManualSignaturePlacement,
					}
				);
			})
			.map((form) => ({
				templateId: form.id,
				name: form.name,
				instruction: form.instruction ?? '',
				type: form.type,
				category: form.category ?? null,
				requiresSignature: form.requiresSignature ?? false,
				useManualSignaturePlacement: form.useManualSignaturePlacement ?? false,
				showStampMetadata: form.showStampMetadata ?? true,
				// Enabling signing is what creates the PARTY role, so a box placed in that same
				// drawer session had nothing to key against and the editor could not save it
				// itself. It travels here, and the backend resolves it once the role exists.
				pendingSignaturePlacements: form.pendingPlacement ? [form.pendingPlacement] : [],
			}));

		await Promise.allSettled([
			isNotEmpty(formsToCreate)
				? createKycFormsTemplatesRequest.mutateAsync(
						{ customDetails: formsToCreate },
						{ onError: () => (isAnyFailed = true) }
					)
				: null,
			isNotEmpty(formsToUpdate)
				? updateKycFormsTemplatesRequest.mutateAsync(
						{ customDetails: formsToUpdate },
						{ onError: () => (isAnyFailed = true) }
					)
				: null,
			isNotEmpty(formsToDelete)
				? deleteCustomDetailsRequest.mutateAsync(
						{ templateIds: formsToDelete, applyToInstances: true },
						{ onError: () => (isAnyFailed = true) }
					)
				: null,
		]);
		mipCreateController?.setIsLoading(false);

		if (isAnyFailed) {
			showToastV2({
				title: tMipCreate('kyc.documents.failed_to_save'),
				state: 'error',
			});
			return { isSuccess: false, formValues };
		}

		return { isSuccess: true, formValues };
	};

	const onLeaveMainKycStep = async (): Promise<WizardSaveResult> => {
		const { isSuccess, formValues } = await saveData();
		if (isSuccess) {
			mipCreateController.removeBackupData(MIP_CREATE_ROUTES.CONTRACTS);
			return SAVE_OK;
		}
		mipCreateController?.updateBackupData(MIP_CREATE_ROUTES.KYC, formValues);
		return SAVE_FAILED;
	};

	useWizardStep(onLeaveMainKycStep);

	useEffect(() => {
		const formValues: KycFormValues = kycForm.getValues();
		const formsTemplates = [
			...formValues.individualFormsTemplates,
			...formValues.pivFormsTemplates,
		];
		const shouldDisableContinue =
			formsTemplates?.some((contract) => contract.name.trim() === '') ?? false;
		mipCreateController?.setDisableContinueButton(shouldDisableContinue);
		mipCreateController?.setPopoverMessage(tCommonUi('kyc.continue_disabled_info'));
	}, [kycForm.watch()]);

	// Unmount only: the effect above re-runs every render, so clearing the flag there would
	// fight the line that sets it.
	useEffect(
		() => () => {
			mipCreateController?.setDisableContinueButton(false);
		},
		[]
	);

	return {
		kycForm,
	};
};
